/**
 * ChatEmptyState — the interactive "what can MoftakiAI do" showcase shown on a
 * fresh conversation, plus ChatIdeasDialog which reopens the same showcase
 * after a conversation has messages (the "✨ Ideas" button in the input row).
 *
 * Cards are grouped by category with per-category icon + tint, NEW badges from
 * the backend payload, staggered entrance animation (reduced-motion aware), a
 * gently pulsing "Try something new" ribbon, and a shuffle button that
 * refetches for variety. A CSV-export example is guaranteed client-side.
 */
import { useEffect, useState, useCallback } from 'react';
import { Box, Typography, Chip, Dialog, DialogTitle, DialogContent, IconButton, Tooltip, CircularProgress } from '@mui/material';
import {
  Search, BarChart, ShowChart, HelpOutline, Description, CalendarMonth, Science,
  Download, RequestQuote, Feedback as FeedbackIcon, Category as CategoryIcon,
  Shuffle, AutoAwesome, Close,
} from '@mui/icons-material';
import { alpha, useTheme, type Theme } from '@mui/material/styles';
import { MoftakiBotIcon } from '../../components/shared/MoftakiBotIcon';
import * as chatApi from '../../services/chatService';
import type { SuggestedQuestion } from '../../services/chatService';
import { groupSuggestions, ensureExportExample, categoryMeta } from './chatSuggestionUtils';

const ICONS: Record<string, React.ReactNode> = {
  search: <Search sx={{ fontSize: 17 }} />,
  list: <Search sx={{ fontSize: 17 }} />,
  assignment: <Description sx={{ fontSize: 17 }} />,
  analytics: <BarChart sx={{ fontSize: 17 }} />,
  chart: <ShowChart sx={{ fontSize: 17 }} />,
  science: <Science sx={{ fontSize: 17 }} />,
  description: <Description sx={{ fontSize: 17 }} />,
  download: <Download sx={{ fontSize: 17 }} />,
  photo: <Description sx={{ fontSize: 17 }} />,
  help: <HelpOutline sx={{ fontSize: 17 }} />,
  schedule: <CalendarMonth sx={{ fontSize: 17 }} />,
  quotes: <RequestQuote sx={{ fontSize: 17 }} />,
  feedback: <FeedbackIcon sx={{ fontSize: 17 }} />,
  catalog: <CategoryIcon sx={{ fontSize: 17 }} />,
};

/** Per-category tint — theme tokens only (no hex literals here). */
function categoryTint(key: string, theme: Theme): string {
  switch (key) {
    case 'scheduling': return theme.schedule.machineTime;
    case 'analytics': return theme.chat.botAccent;
    case 'visualization': return theme.schedule.nowLine;
    case 'documents': return theme.palette.secondary.main;
    case 'quotes': return theme.schedule.maintenance;
    case 'feedback': return theme.schedule.absenceSick;
    case 'catalog': return theme.schedule.outOfService;
    case 'exports': return theme.schedule.absenceVacation;
    case 'help': return theme.schedule.noActivity;
    default: return theme.palette.primary.main;
  }
}

const staggerSx = (index: number) => ({
  '@keyframes suggestion-in': {
    from: { opacity: 0, transform: 'translateY(10px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
  animation: 'suggestion-in 0.3s ease-out both',
  animationDelay: `${Math.min(index, 12) * 0.05}s`,
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
});

interface ShowcaseProps {
  onPick: (text: string) => void;
  /** compact = inside the Ideas dialog (no hero header). */
  compact?: boolean;
}

/** The grouped, animated suggestion grid — shared by empty state + Ideas dialog. */
function SuggestionShowcase({ onPick, compact }: ShowcaseProps) {
  const theme = useTheme();
  const [suggestions, setSuggestions] = useState<SuggestedQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [shuffleSeq, setShuffleSeq] = useState(0);

  const load = useCallback(() => {
    setLoading(true);
    chatApi.getSuggestedQuestions()
      .then((qs) => setSuggestions(ensureExportExample(qs)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load, shuffleSeq]);

  const groups = groupSuggestions(suggestions);
  let cardIndex = -1;

  return (
    <Box sx={{ width: '100%', maxWidth: 760 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1.5 }}>
        {/* Gently pulsing "try something new" ribbon */}
        <Chip icon={<AutoAwesome sx={{ fontSize: 14 }} />} label="Try something new" size="small"
          sx={{
            height: 22, fontSize: '0.66rem', fontWeight: 700,
            backgroundColor: alpha(theme.chat.botAccent, 0.1), color: theme.chat.botAccent,
            '& .MuiChip-icon': { color: theme.chat.botAccent },
            '@keyframes ribbon-pulse': {
              '0%, 100%': { boxShadow: `0 0 0 0 ${alpha(theme.chat.botAccent, 0.25)}` },
              '50%': { boxShadow: `0 0 0 5px ${alpha(theme.chat.botAccent, 0)}` },
            },
            animation: 'ribbon-pulse 2.4s ease-in-out infinite',
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
          }} />
        <Tooltip title="Shuffle suggestions">
          <IconButton size="small" onClick={() => setShuffleSeq((n) => n + 1)} disabled={loading}
            sx={{ color: 'text.secondary' }}>
            {loading ? <CircularProgress size={14} /> : <Shuffle sx={{ fontSize: 16 }} />}
          </IconButton>
        </Tooltip>
      </Box>

      <Box key={shuffleSeq} sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {groups.map((group) => {
          const tint = categoryTint(group.meta.key, theme);
          return (
            <Box key={group.meta.key}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5, px: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', color: tint }}>
                  {ICONS[group.meta.icon] || ICONS.search}
                </Box>
                <Typography variant="caption" sx={{
                  fontSize: '0.66rem', fontWeight: 700, textTransform: 'uppercase',
                  letterSpacing: '0.06em', color: tint,
                }}>
                  {group.meta.label}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {group.items.map((s) => {
                  cardIndex += 1;
                  return (
                    <Box key={s.text} onClick={() => onPick(s.text)} sx={{
                      display: 'flex', alignItems: 'flex-start', gap: 1.25,
                      px: 1.5, py: 1.25, borderRadius: 2, cursor: 'pointer',
                      border: `1px solid ${alpha(theme.palette.divider, 0.4)}`,
                      backgroundColor: theme.palette.background.paper,
                      maxWidth: compact ? '100%' : 350, minWidth: 240, flex: '1 1 280px',
                      position: 'relative', transition: 'all 0.15s ease',
                      ...staggerSx(cardIndex),
                      '&:hover': {
                        borderColor: tint,
                        backgroundColor: alpha(tint, 0.04),
                        boxShadow: `0 2px 10px ${alpha(tint, 0.14)}`,
                        transform: 'translateY(-1px)',
                      },
                    }}>
                      <Box sx={{
                        width: 30, height: 30, borderRadius: '8px', display: 'flex',
                        alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: 0.1,
                        backgroundColor: alpha(tint, 0.1), color: tint,
                      }}>
                        {ICONS[s.icon] || ICONS[categoryMeta(s.category).icon] || ICONS.search}
                      </Box>
                      <Typography variant="body2" sx={{
                        fontSize: '0.8rem', lineHeight: 1.45, color: 'text.primary',
                        pr: s.isNew ? 4 : 0,
                      }}>
                        {s.text}
                      </Typography>
                      {s.isNew && (
                        <Chip label="NEW" size="small" sx={{
                          position: 'absolute', top: 6, right: 6, height: 15,
                          fontSize: '0.52rem', fontWeight: 700,
                          backgroundColor: alpha(theme.chat.botAccent, 0.12), color: theme.chat.botAccent,
                          '& .MuiChip-label': { px: 0.6 },
                        }} />
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

interface EmptyStateProps {
  onNew?: () => void;
  onSuggestionClick: (text: string) => void;
}

/** Full-height empty state for a fresh conversation. */
export function ChatEmptyState({ onNew, onSuggestionClick }: EmptyStateProps) {
  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      minHeight: '100%', justifyContent: 'center', gap: 2.5, px: 3, py: 2,
    }}>
      <Box sx={{ textAlign: 'center' }}>
        <MoftakiBotIcon size={48} />
        <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '1rem', color: 'text.primary' }}>
          What can I help you with?
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: '0.82rem' }}>
          Every test type, quotes, the schedule, time off, feedback, the catalogue — and CSV exports.
        </Typography>
      </Box>

      <SuggestionShowcase onPick={onSuggestionClick} />

      {onNew && (
        <Typography variant="caption" color="primary" sx={{ cursor: 'pointer', fontWeight: 600 }} onClick={onNew}>
          Or start a blank conversation
        </Typography>
      )}
    </Box>
  );
}

interface IdeasDialogProps {
  open: boolean;
  onClose: () => void;
  onPick: (text: string) => void;
}

/** "✨ Ideas" dialog — brings the showcase back once a conversation has messages. */
export function ChatIdeasDialog({ open, onClose, onPick }: IdeasDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        <AutoAwesome color="primary" sx={{ fontSize: 20 }} />
        <Typography variant="subtitle1" component="span" sx={{ fontWeight: 600, flex: 1 }}>
          Ideas — things you can ask
        </Typography>
        <IconButton size="small" onClick={onClose}><Close sx={{ fontSize: 18 }} /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', justifyContent: 'center', pb: 2.5 }}>
        <SuggestionShowcase compact onPick={(t) => { onPick(t); onClose(); }} />
      </DialogContent>
    </Dialog>
  );
}
